import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner, Button, Chip } from '@cowoo/shared/ui-web';
import { ApiProblem, formatDate, formatMoney, newIdempotencyKey, qk } from '@cowoo/shared';
import { api } from '../../lib/api';
import { useAdminStore } from '../../lib/store';
import { Dialog, Field, Panel, PanelRow, fieldClass } from '../../ui';
import { CreditNoteDialog } from './CreditNoteDialog';
import { STATUS_TONE, periodLabel } from './invoiceRow';

/**
 * The invoice panel (10-web-admin § 13).
 *
 * The dunning timeline comes from the server, which derives it from the
 * settings the job actually reads — so what is on screen is what will happen,
 * including the rule that termination is the earlier of day 30 and the end of
 * the month the charge failed in (D-045, BR-PAY-09).
 *
 * There is no edit UI and never will be: an invoice is never changed or deleted
 * (D-046), and a correction is a credit note, which is its own document.
 */
export function InvoicePanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const can = useAdminStore((state) => state.can);
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'credit_note' | 'uncollectible' | null>(null);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<ApiProblem | null>(null);
  const [retried, setRetried] = useState(false);

  const invoice = useQuery({
    queryKey: qk.admin.area('invoices', { id }),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/invoices/{id}', { params: { path: { id } } });
      if (error) throw error;
      return data;
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: qk.admin.area('invoices') });
  const onMutationError = (error: unknown) => setProblem(error instanceof ApiProblem ? error : null);

  const retry = useMutation({
    mutationFn: async () => {
      // A fresh key per press: one press is one intent, and pressing the button
      // again is meant to charge again rather than replay the first answer.
      const { error } = await api.POST('/admin/invoices/{id}/retry', {
        params: { path: { id }, header: { 'idempotency-key': newIdempotencyKey() } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setProblem(null);
      setRetried(true);
      void refresh();
    },
    onError: onMutationError,
  });

  const markUncollectible = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/admin/invoices/{id}/mark-uncollectible', {
        params: { path: { id } },
        body: { reason: reason.trim() },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setProblem(null);
      setDialog(null);
      setReason('');
      void refresh();
    },
    onError: onMutationError,
  });

  /**
   * The PDF opens in a new tab through the signed URL the server answers with.
   *
   * Not `window.open` after the await: Safari blocks a popup that is not the
   * direct result of the click, so the tab is opened first and navigated once
   * the URL arrives (the same detour the member app takes, finding F-06).
   */
  const openPdf = async () => {
    const tab = window.open('', '_blank');
    const { data, error } = await api.GET('/admin/invoices/{id}/pdf', { params: { path: { id } } });
    if (error || !data) {
      tab?.close();
      onMutationError(error);
      return;
    }
    if (tab) tab.location.href = data.url;
  };

  const data = invoice.data;
  if (!data)
    return <Panel label={t('admin.invoices.panelLabel')} title="—" onClose={onClose} children={null} />;

  const creditedCents = data.creditNotes.reduce((sum, note) => sum + note.grossCents, 0);
  const remainingCents = data.grossCents - creditedCents;

  return (
    <Panel
      label={t('admin.invoices.panelLabel', { number: data.number })}
      title={formatMoney(data.grossCents)}
      subtitle={[data.customerNumber, data.customerName].filter(Boolean).join(' · ')}
      onClose={onClose}
    >
      <div className="flex items-center gap-2">
        <Chip tone={STATUS_TONE[data.status] ?? 'neutral'} label={t(`admin.invoices.state.${data.status}`)} />
        <Chip tone="neutral" label={periodLabel(data)} />
      </div>

      <div>
        <PanelRow label={t('admin.invoices.column.date')} value={formatDate(data.issuedOn)} />
        <PanelRow label={t('admin.invoices.column.net')} value={formatMoney(data.netCents)} />
        <PanelRow label={t('admin.invoices.column.vat')} value={formatMoney(data.vatCents)} />
        <PanelRow label={t('admin.invoices.column.gross')} value={formatMoney(data.grossCents)} />
        <PanelRow label={t('admin.invoices.payment')} value={data.paymentNote ?? '—'} />
        {data.userId ? (
          <PanelRow
            label={t('admin.invoices.member')}
            value={<Link to={`/mitglieder/${data.userId}`}>{data.customerName ?? '—'}</Link>}
          />
        ) : null}
      </div>

      <div>
        <p className="text-label text-muted pb-1.5 uppercase">{t('admin.invoices.lines')}</p>
        <table className="text-adminSmall w-full">
          <tbody>
            {data.lines.map((line, index) => (
              <tr key={`${line.description}-${index}`} className="border-hairline border-b">
                <td className="py-1.5">
                  {line.quantity > 1 ? `${line.quantity} × ` : ''}
                  {line.description}
                </td>
                <td className="py-1.5 text-right tabular-nums">{formatMoney(line.grossCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.dunning.length > 0 ? (
        <div className="bg-fog rounded-input flex flex-col gap-2 px-3.5 py-3">
          <p className="text-adminBody text-ink font-semibold">{t('admin.invoices.dunning')}</p>
          <ul className="text-adminSmall flex flex-col gap-1.5">
            {data.dunning.map((step) => (
              <li key={`${step.day}-${step.step}`} className="flex items-start gap-2.5">
                <span
                  className={`rounded-pill mt-1.5 h-2 w-2 shrink-0 ${
                    step.done ? 'bg-danger-text' : 'bg-line-strong'
                  }`}
                  aria-hidden
                />
                <span className={step.done ? 'text-text' : 'text-muted'}>
                  <strong>{formatDate(step.day)}</strong> — {t(`admin.invoices.dunningStep.${step.step}`)}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-adminSmall text-muted">{t('admin.invoices.dunningHint')}</p>
        </div>
      ) : null}

      {data.creditNotes.length > 0 ? (
        <div>
          <p className="text-label text-muted pb-1.5 uppercase">{t('admin.invoices.creditNotes')}</p>
          <ul className="text-adminSmall text-muted flex flex-col gap-1">
            {data.creditNotes.map((note) => (
              <li key={note.id}>
                {note.number} · {formatMoney(note.grossCents)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}
      {retried ? <Banner tone="success">{t('admin.invoices.retryDone')}</Banner> : null}

      <div className="mt-auto grid grid-cols-2 gap-2">
        <Button size="sm" variant="secondary" onClick={() => void openPdf()}>
          {t('admin.invoices.openPdf')}
        </Button>
        {data.status === 'open' ? (
          <Button size="sm" loading={retry.isPending} onClick={() => retry.mutate()}>
            {t('admin.invoices.retry')}
          </Button>
        ) : null}
        {/* Nothing left to credit once the invoice is fully credited. */}
        {remainingCents > 0 ? (
          <Button size="sm" variant="secondary" onClick={() => setDialog('credit_note')}>
            {t('admin.invoices.createCreditNote')}
          </Button>
        ) : null}
        {/* § 13 — writing an invoice off is owner work. */}
        {can('gdpr') && data.status === 'open' ? (
          <Button size="sm" variant="danger" onClick={() => setDialog('uncollectible')}>
            {t('admin.invoices.markUncollectible')}
          </Button>
        ) : null}
      </div>
      <p className="text-adminSmall text-muted">{t('admin.invoices.neverEdited')}</p>

      {dialog === 'credit_note' ? (
        <CreditNoteDialog invoiceId={id} remainingCents={remainingCents} onClose={() => setDialog(null)} />
      ) : null}

      {dialog === 'uncollectible' ? (
        <Dialog
          title={t('admin.invoices.uncollectible.title')}
          intro={t('admin.invoices.uncollectible.intro')}
          tone="danger"
          confirmLabel={t('admin.invoices.uncollectible.confirm')}
          onConfirm={() => markUncollectible.mutate()}
          onClose={() => setDialog(null)}
          busy={markUncollectible.isPending}
          disabled={reason.trim().length < 5}
        >
          {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}
          <Field label={t('admin.invoices.creditNote.reason')} hint={t('admin.reasonHint')}>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('admin.reasonPlaceholder')}
              className={fieldClass}
            />
          </Field>
        </Dialog>
      ) : null}
    </Panel>
  );
}
